import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, MoreVertical, Eye, EyeOff, Trash2, Building2, MapPin, ExternalLink, FileSearch, Clock, HardHat, CheckCircle, MessageSquare, RotateCcw, XCircle, ChevronDown, ChevronUp, Send, Pencil } from 'lucide-react';

const historyActionConfig: Record<string, { label: string; dot: string; icon: any }> = {
  feedback_sent: { label: 'QC sent feedback', dot: 'bg-amber-500', icon: MessageSquare },
  resubmitted: { label: 'You resubmitted', dot: 'bg-blue-500', icon: RotateCcw },
  approved: { label: 'Approved by QC', dot: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected by QC', dot: 'bg-red-500', icon: XCircle },
};

const approvalColors: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  pending_review: 'bg-yellow-100 text-yellow-800',
  approved: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
};

const projectStatusIcons: Record<string, { icon: any; color: string }> = {
  upcoming: { icon: Clock, color: 'text-blue-500' },
  under_construction: { icon: HardHat, color: 'text-orange-500' },
  ready: { icon: CheckCircle, color: 'text-green-500' },
};

export function MyProjects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [filterTab, setFilterTab] = useState('all');
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [expandedHistory, setExpandedHistory] = useState<Record<string, boolean>>({});

  const { data: devProfile } = useQuery({
    queryKey: ['my-dev-profile', user?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('developer_profiles')
        .select('id')
        .eq('user_id', user!.id)
        .single();
      if (error) throw error;
      return data;
    },
    enabled: !!user,
  });

  const { data: projects, isLoading } = useQuery({
    queryKey: ['my-projects', devProfile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('projects')
        .select('*, project_images(url, is_primary)')
        .eq('developer_id', devProfile!.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: !!devProfile,
  });

  // Fetch review history for all dev's projects in one query
  const projectIds = (projects || []).map((p: any) => p.id);
  const { data: allHistory } = useQuery({
    queryKey: ['my-projects-review-history', projectIds.join(',')],
    queryFn: async () => {
      if (projectIds.length === 0) return [];
      const { data, error } = await supabase
        .from('project_review_history' as any)
        .select('*')
        .in('project_id', projectIds)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    },
    enabled: projectIds.length > 0,
  });

  const historyByProject: Record<string, any[]> = {};
  (allHistory || []).forEach((h: any) => {
    (historyByProject[h.project_id] ||= []).push(h);
  });

  const resubmitMutation = useMutation({
    mutationFn: async (project: any) => {
      if (!user) throw new Error('Not authenticated');
      const { error: updErr } = await supabase
        .from('projects')
        .update({ approval_status: 'pending_review' })
        .eq('id', project.id);
      if (updErr) throw updErr;

      await supabase.from('project_review_history' as any).insert({
        project_id: project.id,
        reviewer_id: user.id,
        action: 'resubmitted',
        notes: null,
      });

      // Notify the last reviewer (if any) that the project is back
      const projHistory = historyByProject[project.id] || [];
      const lastReviewerEntry = projHistory.find(
        (h: any) => h.action === 'feedback_sent' || h.action === 'rejected' || h.action === 'approved'
      );
      const projName = getName(project.name);
      if (lastReviewerEntry?.reviewer_id) {
        await supabase.from('notifications').insert({
          user_id: lastReviewerEntry.reviewer_id,
          type: 'project_resubmitted',
          title: `Project resubmitted: ${projName}`,
          message: `The developer has addressed your feedback and resubmitted "${projName}" for review.`,
          metadata: { project_id: project.id, action: 'resubmitted' },
        });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      queryClient.invalidateQueries({ queryKey: ['my-projects-review-history'] });
      toast.success('Project resubmitted for review.');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, updates }: { id: string; updates: Record<string, any> }) => {
      const { error } = await supabase.from('projects').update(updates as any).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      toast.success('Project updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      // Delete related data first
      await supabase.from('project_images').delete().eq('project_id', id);
      await supabase.from('project_features').delete().eq('project_id', id);
      await supabase.from('project_unit_types').delete().eq('project_id', id);
      await supabase.from('project_updates').delete().eq('project_id', id);
      await supabase.from('project_payment_plans').delete().eq('project_id', id);
      await supabase.from('project_payment_plan_images' as any).delete().eq('project_id', id);
      const { error } = await supabase.from('projects').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-projects'] });
      toast.success('Project deleted');
      setDeleteTarget(null);
    },
    onError: (err: any) => toast.error(err.message),
  });

  const getName = (val: any) => typeof val === 'object' ? (val?.en || '') : (val || '');

  const filtered = projects?.filter((p: any) => {
    if (filterTab === 'all') return true;
    if (filterTab === 'approved') return p.approval_status === 'approved';
    if (filterTab === 'pending') return p.approval_status === 'pending_review' || p.approval_status === 'draft';
    return false;
  }) || [];

  const counts = {
    all: projects?.length || 0,
    approved: projects?.filter((p: any) => p.approval_status === 'approved').length || 0,
    pending: projects?.filter((p: any) => p.approval_status === 'pending_review' || p.approval_status === 'draft').length || 0,
  };

  if (!devProfile) {
    return (
      <div className="text-center py-12">
        <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
        <p className="text-muted-foreground">Developer profile not found.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold">My Projects</h2>
        <Button size="sm" onClick={() => navigate('/dashboard/projects/new')}>
          <Plus className="h-4 w-4 mr-1" /> Add Project
        </Button>
      </div>

      <Tabs value={filterTab} onValueChange={setFilterTab}>
        <TabsList>
          <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
          <TabsTrigger value="approved">Approved ({counts.approved})</TabsTrigger>
          <TabsTrigger value="pending">Pending/Draft ({counts.pending})</TabsTrigger>
        </TabsList>
      </Tabs>

      {isLoading ? (
        <div className="space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-20 rounded-xl" />)}</div>
      ) : filtered.length > 0 ? (
        <div className="space-y-3">
          {filtered.map((proj: any) => {
            const name = getName(proj.name);
            const img = proj.project_images?.find((i: any) => i.is_primary)?.url || proj.project_images?.[0]?.url;
            const isApproved = proj.approval_status === 'approved';
            const si = projectStatusIcons[proj.status] || projectStatusIcons.upcoming;
            const StatusIcon = si.icon;
            const projHistory = historyByProject[proj.id] || [];
            const lastFeedback = projHistory.find((h: any) => h.action === 'feedback_sent' || h.action === 'rejected');
            const lastResubmit = projHistory.find((h: any) => h.action === 'resubmitted');
            const hasUnresolvedFeedback = !!lastFeedback && (
              !lastResubmit || new Date(lastFeedback.created_at) > new Date(lastResubmit.created_at)
            );
            const canResubmit = proj.approval_status === 'rejected' || (proj.approval_status === 'pending_review' && hasUnresolvedFeedback);
            const showHistory = expandedHistory[proj.id];

            return (
              <Card key={proj.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4 space-y-3">
                  <div className="flex items-center gap-4">
                    <div className="h-14 w-14 rounded-lg bg-muted overflow-hidden shrink-0">
                      {img ? <img src={img} alt={name} className="h-full w-full object-cover" /> : <Building2 className="h-6 w-6 text-muted-foreground m-auto mt-4" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold truncate">{name}</span>
                        <StatusIcon className={`h-4 w-4 ${si.color}`} />
                      </div>
                      <div className="text-sm text-muted-foreground flex items-center gap-1">
                        <MapPin className="h-3 w-3" /> {proj.city}{proj.sub_city ? `, ${proj.sub_city}` : ''}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {proj.total_units || 0} units • {proj.available_units || 0} available • {proj.status?.replace('_', ' ')}
                      </div>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={approvalColors[proj.approval_status] || approvalColors.draft}>
                        {proj.approval_status === 'pending_review' ? 'Pending' : proj.approval_status}
                      </Badge>
                      {isApproved && (
                        <Badge variant="outline" className={proj.is_featured ? 'border-yellow-400 text-yellow-700' : ''}>
                          {proj.is_featured ? '⭐ Featured' : 'Live'}
                        </Badge>
                      )}
                      {proj.has_pending_changes && (
                        <Badge variant="outline" className="border-blue-400 text-blue-700 bg-blue-50">
                          Edits pending QC
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-1">
                      <Link to={`/projects/${proj.id}`} target="_blank">
                        <Button variant="ghost" size="sm"><ExternalLink className="h-4 w-4" /></Button>
                      </Link>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon"><MoreVertical className="h-4 w-4" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(`/dashboard/projects/${proj.id}/edit`)}>
                            <Pencil className="h-4 w-4 mr-2" /> Edit Project
                          </DropdownMenuItem>
                          {isApproved && proj.approval_status === 'approved' && (
                            <>
                              {proj.status === 'upcoming' && (
                                <DropdownMenuItem onClick={() => statusMutation.mutate({ id: proj.id, updates: { status: 'under_construction' } })}>
                                  <HardHat className="h-4 w-4 mr-2" /> Mark Under Construction
                                </DropdownMenuItem>
                              )}
                              {(proj.status === 'upcoming' || proj.status === 'under_construction') && (
                                <DropdownMenuItem onClick={() => statusMutation.mutate({ id: proj.id, updates: { status: 'ready' } })}>
                                  <CheckCircle className="h-4 w-4 mr-2" /> Mark as Ready
                                </DropdownMenuItem>
                              )}
                            </>
                          )}
                          {proj.approval_status === 'draft' && (
                            <DropdownMenuItem onClick={() => statusMutation.mutate({ id: proj.id, updates: { approval_status: 'pending_review' } })}>
                              <Eye className="h-4 w-4 mr-2" /> Submit for Approval
                            </DropdownMenuItem>
                          )}
                          {proj.approval_status === 'rejected' && (
                            <DropdownMenuItem onClick={() => statusMutation.mutate({ id: proj.id, updates: { approval_status: 'pending_review' } })}>
                              <Eye className="h-4 w-4 mr-2" /> Resubmit for Approval
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem className="text-destructive" onClick={() => setDeleteTarget(proj)}>
                            <Trash2 className="h-4 w-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {(proj.reviewer_notes || projHistory.length > 0) && (
                    <div className={`rounded-md border-l-4 p-3 text-sm ${
                      proj.approval_status === 'rejected'
                        ? 'border-l-red-500 bg-red-50 text-red-900'
                        : proj.approval_status === 'approved'
                          ? 'border-l-green-500 bg-green-50 text-green-900'
                          : 'border-l-yellow-500 bg-yellow-50 text-yellow-900'
                    }`}>
                      <div className="flex items-start gap-2">
                        <MessageSquare className="h-4 w-4 mt-0.5 shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2 mb-1">
                            <div className="font-semibold text-xs uppercase tracking-wide">
                              {hasUnresolvedFeedback ? 'Action needed: QC feedback' : 'QC Review Feedback'}
                            </div>
                            {projHistory.length > 0 && (
                              <button
                                type="button"
                                onClick={() => setExpandedHistory(p => ({ ...p, [proj.id]: !p[proj.id] }))}
                                className="text-xs underline opacity-80 hover:opacity-100 flex items-center gap-1"
                              >
                                {showHistory ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
                                {showHistory ? 'Hide' : 'Show'} history ({projHistory.length})
                              </button>
                            )}
                          </div>
                          {proj.reviewer_notes && <p className="whitespace-pre-wrap">{proj.reviewer_notes}</p>}

                          {showHistory && projHistory.length > 0 && (
                            <div className="mt-3 space-y-2 border-t border-current/20 pt-3">
                              {projHistory.map((h: any) => {
                                const cfg = historyActionConfig[h.action] || historyActionConfig.feedback_sent;
                                const Icon = cfg.icon;
                                return (
                                  <div key={h.id} className="flex gap-2 items-start">
                                    <div className={`h-2 w-2 rounded-full ${cfg.dot} mt-1.5 shrink-0`} />
                                    <div className="flex-1 text-xs">
                                      <div className="flex items-center gap-1.5 font-medium">
                                        <Icon className="h-3 w-3" />
                                        <span>{cfg.label}</span>
                                        <span className="opacity-60">• {new Date(h.created_at).toLocaleString()}</span>
                                      </div>
                                      {h.notes && <p className="opacity-90 whitespace-pre-wrap mt-0.5">{h.notes}</p>}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {canResubmit && (
                            <div className="mt-3 flex flex-wrap gap-2">
                              <Button
                                size="sm"
                                onClick={() => resubmitMutation.mutate(proj)}
                                disabled={resubmitMutation.isPending}
                              >
                                <Send className="h-3.5 w-3.5 mr-1" /> Resubmit for Review
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-20 text-muted-foreground">
          <p>No projects yet. Create your first project to get started.</p>
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Delete Project</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to delete "<strong>{deleteTarget && getName(deleteTarget.name)}</strong>"? This will remove all associated data (images, units, features, updates, payment plans). This cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={() => deleteMutation.mutate(deleteTarget.id)} disabled={deleteMutation.isPending}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
